import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const TOKEN_KEY = 'token';
  const TOKEN_EXPIRY_KEY = 'token_expires_at';
  const TOKEN_TTL_DAYS = parseInt(process.env.REACT_APP_AUTH_TOKEN_TTL_DAYS || '180', 10);
  const TOKEN_TTL_MS = Number.isFinite(TOKEN_TTL_DAYS)
    ? TOKEN_TTL_DAYS * 24 * 60 * 60 * 1000
    : 180 * 24 * 60 * 60 * 1000;
  const getStoredToken = () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      return null;
    }
    const expiryValue = Number(localStorage.getItem(TOKEN_EXPIRY_KEY));
    const now = Date.now();
    if (Number.isFinite(expiryValue) && expiryValue > 0) {
      if (now > expiryValue) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        return null;
      }
    } else {
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(now + TOKEN_TTL_MS));
    }
    return storedToken;
  };
  const [token, setTokenState] = useState(getStoredToken());

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setTokenState(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const setToken = (newToken) => {
    if (!newToken) {
      logout();
      return;
    }
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
    setTokenState(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      const userData = response.data;
      setUser(userData);

      // Clear merchant flags if logged in as regular user
      if (userData && userData.role !== 'merchant') {
        localStorage.removeItem('merchantIntent');
        localStorage.removeItem('locuUserType');
        sessionStorage.removeItem('isMerchant');
      } else if (userData && userData.role === 'merchant') {
        // Set merchant view for restaurant users
        localStorage.setItem('locuUserType', 'merchants');
      }
    } catch (error) {
      console.error('Fetch user error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []); // logout and setState are stable

  useEffect(() => {
    if (token) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(err => err.msg || err.message).join(', ');
        return {
          success: false,
          message: errorMessages || 'Validation failed'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const register = async (name, email, password, role, phone, dateOfBirth, gender) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        name,
        email,
        password,
        role,
        phone,
        dateOfBirth,
        gender
      });
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle network errors (server not running, CORS, etc.)
      if (!error.response) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure the backend server is running on port 5000.'
        };
      }
      
      // Handle validation errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(err => err.msg || err.message).join(', ');
        return {
          success: false,
          message: errorMessages || 'Validation failed'
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Registration failed'
      };
    }
  };

  const value = {
    user,
    setUser,
    setToken,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    refreshUser: fetchUser // Expose fetchUser to refresh user data
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

